import { useLocalization } from "@fluent/react";
import { useAppDispatch, useDataGridCustomLocaleText } from "../../app/hooks";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Paper, TableContainer } from "@mui/material";
import { LoadingContainer } from "../../app/LoadingContainer";
import { ErrorContainer } from "../../app/ErrorContainer";
import { FlexDiv } from "../../app/Utils";
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar";
import { AddFabButton } from "../../app/components/AddFabButton";
import { CounterpartyListItem, loadCounterparties } from "./counterpartyApi";
import { CreateCounterpartyDialog } from "./dialogs/CreateCounterpartyDialog";


export const CounterpartiesList = () => {
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [counterparties, setCounterparties] = useState<CounterpartyListItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const localeText = useDataGridCustomLocaleText();
    const [openCreate, setOpenCreate] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadCounterparties())
            .then(unwrapResult)
            .then(setCounterparties)
            .catch((error) => {
                console.error(error);
                setIsError(true);
                setError(error);
            })
            .finally(() => setIsLoading(false));
    }, [])

    const handleOpen = () => setOpenCreate(true);
    const handleCancel = () => setOpenCreate(false);

    const handleSuccess = (counterparty: { publicId: string }) => {
        setOpenCreate(false);
        navigate(`/suppliercounterparts/edit/${counterparty.publicId}`);
    };


    const handleGoToClick = (id) => {
        navigate(`/suppliercounterparts/edit/${id}`)
    };

    const columns: GridColDef[] = [
        {
            field: "displayName",
            headerName: l10n.getString("counterparties-displayName", null, "_displayName"),
            flex: 1,
            minWidth: 220,
        },
        {
            field: "countryCode",
            headerName: l10n.getString("counterparties-country", null, "_country"),
            width: 110,
        },
        {
            field: "primaryEmail",
            headerName: l10n.getString("counterparties-email", null, "_email"),
            flex: 1,
            minWidth: 200,
        },
        {
            field: "primaryPhone",
            headerName: l10n.getString("counterparties-phone", null, "_phone"),
            width: 160,
        },
        {
            field: "accountManagerFullName",
            headerName: l10n.getString("counterparties-account-manager", null, "Account manager"),
            flex: 1,
            minWidth: 180,
            valueGetter: (value) => value ?? "",
        },
        {
            field: "roles",
            headerName: l10n.getString("counterparties-roles", null, "_roles"),
            flex: 1,
            minWidth: 180,
            sortable: false,
            renderCell: (params) =>
                params.value?.join(", ") ?? ""
        },
        {
            field: "isVip",
            headerName: l10n.getString("counterparties-vip", null, "_vip"),
            width: 80,
            type: "boolean",
        }
    ];

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }


    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    return (
        <>
            <TableContainer component={Paper}>
                <div style={{ display: 'flex', height: '100%' }}>
                    <div style={{ flexGrow: 1 }}>
                        <DataGrid
                            style={{ minHeight: 100, height: 'calc(100vh - 200px)' }}
                            localeText={localeText}
                            getRowId={(row) => row.publicId}
                            rows={counterparties}
                            columns={columns}
                            slots={{ toolbar: QuickSearchToolbar }}
                            initialState={{
                                sorting: { sortModel: [{ field: 'createDate', sort: 'desc' }] }
                            }}
                            onRowDoubleClick={({ row }) => handleGoToClick(row.publicId)} />
                    </div>
                </div>
                <FlexDiv>
                    <AddFabButton
                        onClick={handleOpen}
                    />
                </FlexDiv>
            </TableContainer>
            {openCreate && (
                <CreateCounterpartyDialog
                    onCancel={handleCancel}
                    onSuccess={handleSuccess}
                />
            )}
        </>
    )
}
